import {
  Editable,
  EditableInput,
  EditablePreview,
  HStack,
  IconButton,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuList,
  type SystemStyleObject,
} from '@chakra-ui/react';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

export interface SubNavItemProps {
  to: string;
  children: ReactNode;
  menuContent?: ReactNode;
  'data-testid'?: string;
  onClick?: () => void;
  linkStyles?: SystemStyleObject;
  containerStyles?: SystemStyleObject;
  editing?: boolean;
  editableValue?: string;
  onRename?: (val: string) => void;
}

export const SubNavItem = ({
  to,
  children,
  menuContent,
  onClick,
  editing = false,
  editableValue,
  onRename,
  ...props
}: SubNavItemProps) => {
  return (
    <HStack
      w="100%"
      spacing={0}
      alignItems="stretch"
      _hover={{ background: 'gray.100' }}
      borderRadius="md"
      role="group"
      mt="8px"
    >
      {editing ? (
        <Editable
          defaultValue={editableValue}
          startWithEditView
          flexGrow={1}
          fontSize="md"
          color="text.primary"
          isPreviewFocusable={false}
          onSubmit={onRename}
        >
          <EditablePreview w="100%" textDecoration="none" />
          <Input as={EditableInput} w="100%" variant="flushed" pl="12px" />
        </Editable>
      ) : (
        <Link
          className="rl-sub-nav-link-item"
          as={NavLink}
          flexGrow={1}
          to={to}
          onClick={onClick}
          fontSize="md"
          padding="8px 12px"
          color="text.primary"
          borderRadius="md"
          _hover={{ textDecoration: 'none' }}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          data-testid={props['data-testid']}
        >
          {children}
        </Link>
      )}
      {menuContent && (
        <Menu placement="bottom-end" offset={[0, 0]}>
          <MenuButton
            as={IconButton}
            icon={<IconDotGrid1x3Vertical size={14} />}
            size="sm"
            height="32px"
            width="32px"
            alignSelf="center"
            aria-label="Actions"
            data-testid={`${props['data-testid'] ?? 'sub-nav-item'}-actions`}
            variant="ghost"
            visibility={{ base: 'visible', md: 'hidden' }}
            marginRight="1"
            borderRadius="4"
            _groupHover={{ visibility: 'visible' }}
            ml="auto"
          />
          <MenuList
            minW="124px"
            data-testid={`${props['data-testid'] ?? 'sub-nav-item'}-menu-list`}
          >
            {menuContent}
          </MenuList>
        </Menu>
      )}
    </HStack>
  );
};
